import { loadConnectAndInitialize } from '@stripe/connect-js';
import {
  ConnectAccountManagement,
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
  ConnectNotificationBanner,
} from '@stripe/react-connect-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { api } from '../../lib/api';
import { useCan, useFeature } from '../../stores/session';

/**
 * A design token, resolved to the literal value behind it.
 *
 * For the one consumer that cannot take a `var()`: Stripe's embedded components
 * live in an iframe of their own, where our custom properties do not exist. The
 * fallback is what the panel used before these were read from the stylesheet,
 * and covers a context with no computed style at all.
 */
function token(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;

  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

interface Status {
  connected: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  requirements: string[];
}

/**
 * Whether this account can be paid yet.
 *
 * `undefined` while it is unknown - payments off, not a host, or the request
 * still in flight. Callers use it to *add* a warning, never to hide something,
 * so an unknown answer stays quiet rather than guessing in either direction.
 *
 * Shares a query key with the panel below, so a page showing both asks once.
 */
export function usePayoutsReady(): boolean | undefined {
  const enabled = useFeature('payments');
  const isHost = useCan('host.broadcast');

  const { data } = useQuery({
    queryKey: ['stripe', 'status'],
    queryFn: () => api.get<Status>('/me/stripe'),
    enabled: enabled && isHost,
  });

  if (!enabled || !isHost || !data) {
    return undefined;
  }

  return data.payouts_enabled;
}

/**
 * Connecting a Stripe account, so a host can be paid.
 *
 * ## Why this is embedded rather than a redirect
 *
 * Stripe carries the losses on these accounts, and that configuration *requires*
 * embedded components - onboarding, account management and the notification
 * banner. It is not a styling preference: with Stripe collecting requirements,
 * hosted account-update links are not available for these accounts at all.
 *
 * It has a second consequence worth knowing, because it shapes every support
 * conversation that will ever happen about this: **the platform cannot fill
 * anything in on a host's behalf.** Writing `business_profile` on a connected
 * account returns 403. Everything a host is asked for, they must enter here.
 *
 * ## The secret is fetched, never stored
 *
 * `fetchClientSecret` is called by Stripe's script when it needs one, including
 * when the old one expires mid-session. Holding one in state would work until
 * somebody left the tab open over lunch.
 */
export function PayoutsPanel() {
  const enabled = useFeature('payments');
  const isHost = useCan('host.broadcast');
  const queryClient = useQueryClient();
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['stripe', 'status'],
    queryFn: () => api.get<Status>('/me/stripe'),
    enabled: enabled && isHost,
  });

  /**
   * Initialised once, and only once there is something to show.
   *
   * `useMemo` rather than state: `loadConnectAndInitialize` mounts an iframe and
   * calling it twice gives two, which race over the same account.
   */
  const connect = useMemo(() => {
    const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

    if (!key || !data?.connected) return null;

    return loadConnectAndInitialize({
      publishableKey: key,
      fetchClientSecret: async () => {
        const session = await api.post<{ client_secret: string }>('/me/stripe/account');

        return session.client_secret;
      },
      /*
       * Stripe's components render in an iframe we cannot style, so the only
       * lever is this palette. A bright white form in the middle of a dark page
       * reads as a broken embed rather than as part of the product.
       *
       * Read from the stylesheet rather than written out again. Stripe needs
       * literal colours - an iframe cannot resolve our custom properties - but
       * that is a reason to *resolve* them here, not a reason to keep a second
       * copy of the palette that nothing would update when the first one
       * changed. It also means the embed follows a theme switch for free.
       */
      appearance: {
        overlays: 'dialog',
        variables: {
          colorPrimary: token('--color-accent', '#7c7cf0'),
          colorBackground: token('--color-raised', '#12122a'),
          colorText: token('--color-ink', '#e8e8f5'),
          colorSecondaryText: token('--color-soft', '#9a9ab8'),
          colorBorder: token('--color-frame', '#26264d'),
          borderRadius: '10px',
        },
      },
    });
  }, [data?.connected]);

  // Nothing to say to somebody this cannot apply to.
  if (!enabled || !isHost) return null;

  const start = async () => {
    setStarting(true);
    setFailed(false);

    try {
      await api.post('/me/stripe/account');
      await queryClient.invalidateQueries({ queryKey: ['stripe', 'status'] });
    } catch {
      setFailed(true);
    } finally {
      setStarting(false);
    }
  };

  return (
    <Card className="p-6" data-payouts-panel>
      <h2 className="mb-1 text-sm font-medium uppercase tracking-wider text-faint">Payouts</h2>

      {isPending ? (
        <p className="font-mono text-[10px] text-ghost">loading…</p>
      ) : !data?.connected ? (
        <>
          <p className="mb-4 text-sm text-soft">
            Connect a Stripe account to receive tips and paid boosts. Stripe handles the
            identity checks and pays you directly — we never hold your money.
          </p>

          {failed && (
            <div className="mb-3">
              <Alert tone="error">That did not start. Try again in a moment.</Alert>
            </div>
          )}

          <Button onClick={start} loading={starting} data-connect-stripe>
            Set up payouts
          </Button>
        </>
      ) : (
        <>
          <Ready status={data} />

          {connect && (
            <ConnectComponentsProvider connectInstance={connect}>
              {/*
                Stripe's own messages to the host - outstanding requirements,
                verification results, deadlines. Required with this account
                configuration, and the only channel Stripe has to reach them,
                since these accounts have no Stripe dashboard of their own.
              */}
              <div className="mt-4">
                <ConnectNotificationBanner />
              </div>

              <div className="mt-4">
                {data.payouts_enabled ? (
                  <ConnectAccountManagement />
                ) : (
                  <ConnectAccountOnboarding
                    onExit={() => {
                      void queryClient.invalidateQueries({ queryKey: ['stripe', 'status'] });
                    }}
                  />
                )}
              </div>
            </ConnectComponentsProvider>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * Where the host stands, in a sentence.
 *
 * Stripe's own component says what is outstanding in detail; this exists so the
 * answer to "can I be paid yet" is legible without reading a form.
 */
function Ready({ status }: { status: Status }) {
  if (status.payouts_enabled) {
    return (
      <p className="text-sm text-soft">
        <span className="font-medium text-fresh">Ready.</span> Tips and boosts will be paid to
        your Stripe account. Details below if you need to change anything.
      </p>
    );
  }

  const count = status.requirements.length;

  return (
    <p className="text-sm text-soft">
      <span className="font-medium text-hot-text">Not finished.</span>{' '}
      {status.details_submitted
        ? 'Stripe is reviewing your details.'
        : `Stripe needs ${count === 0 ? 'a few more details' : `${count} more ${count === 1 ? 'detail' : 'details'}`} before you can be paid.`}{' '}
      You can complete it below.
    </p>
  );
}
